import * as signalR from "@microsoft/signalr";
import API_CONFIG from "../../config/api";

/**
 * SignalR Chat Hub Service
 * Manages WebSocket connection for real-time incident chat
 */
class ChatHubService {
  constructor() {
    this.connection = null;
    this.connectionState = "disconnected"; // disconnected, connecting, connected, reconnecting
    this.currentIncidentId = null;
    this.messageCallbacks = [];
    this.streamingCallbacks = [];
    this.connectionStateCallbacks = [];
    this.errorCallbacks = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.isConnecting = false; // Flag to prevent concurrent connection attempts
  }

  /**
   * Get Clerk JWT token for authentication
   */
  async getAuthToken() {
    try {
      const clerk = window.Clerk;
      if (clerk?.session) {
        return await clerk.session.getToken();
      }
      return null;
    } catch (error) {
      console.warn("Failed to get Clerk token for SignalR:", error);
      return null;
    }
  }

  /**
   * Build the hub URL with authentication
   */
  getHubUrl() {
    return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHAT_HUB}`;
  }

  /**
   * Start SignalR connection for a specific incident
   * @param {string|number} incidentId - The incident ID to join
   */
  async startConnection(incidentId) {
    // If already connected to same incident, skip
    if (
      this.connection &&
      this.currentIncidentId === incidentId &&
      this.connection.state === signalR.HubConnectionState.Connected
    ) {
      console.log("Already connected to incident:", incidentId);
      return;
    }

    // If currently connecting, skip to prevent duplicate connections
    if (this.isConnecting) {
      console.log("Connection already in progress, skipping...");
      return;
    }

    // If connection is in connecting state, skip
    if (this.connection?.state === signalR.HubConnectionState.Connecting) {
      console.log("Connection is already connecting, skipping...");
      return;
    }

    this.isConnecting = true;

    // Stop existing connection if connecting to different incident
    if (this.connection) {
      await this.stopConnection();
    }

    this.currentIncidentId = incidentId;
    this.updateConnectionState("connecting");

    try {
      const token = await this.getAuthToken();

      // Build connection with configuration
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(this.getHubUrl(), {
          accessTokenFactory: () => token,
          transport:
            signalR.HttpTransportType.WebSockets |
            signalR.HttpTransportType.ServerSentEvents |
            signalR.HttpTransportType.LongPolling,
          withCredentials: true,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff: 0s, 2s, 4s, 8s, 16s, then stop
            if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
              return null; // Stop reconnecting
            }
            return Math.min(
              1000 * Math.pow(2, retryContext.previousRetryCount),
              30000
            );
          },
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Set up event handlers
      this.setupEventHandlers();

      // Start connection
      await this.connection.start();
      console.log("SignalR Connected to hub");

      // Join the incident room
      await this.joinIncidentRoom(incidentId);

      this.reconnectAttempts = 0;
      this.updateConnectionState("connected");
    } catch (error) {
      console.error("SignalR Connection Error:", error);
      this.updateConnectionState("disconnected");
      this.notifyError(error);
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Set up SignalR event handlers
   */
  setupEventHandlers() {
    if (!this.connection) return;

    // Handle receiving complete messages
    this.connection.on("ReceiveMessage", (message) => {
      console.log("Received message:", message);
      this.messageCallbacks.forEach((callback) => callback(message));
    });

    // Handle receiving streaming tokens (word-by-word)
    this.connection.on("ReceiveStreamingToken", (token) => {
      this.streamingCallbacks.forEach((callback) => callback(token));
    });

    // Handle streaming complete
    this.connection.on("StreamingComplete", (messageId) => {
      this.streamingCallbacks.forEach((callback) =>
        callback({ type: "complete", messageId })
      );
    });

    // Handle connection errors from server
    this.connection.on("Error", (errorMessage) => {
      console.error("Server error:", errorMessage);
      this.notifyError(new Error(errorMessage));
    });

    // Handle user joined notification
    this.connection.on("UserJoined", (userId, incidentId) => {
      console.log(`User ${userId} joined incident ${incidentId}`);
    });

    // Handle user left notification
    this.connection.on("UserLeft", (userId, incidentId) => {
      console.log(`User ${userId} left incident ${incidentId}`);
    });

    // Handle reconnecting
    this.connection.onreconnecting((error) => {
      console.log("SignalR Reconnecting...", error);
      this.updateConnectionState("reconnecting");
    });

    // Handle reconnected
    this.connection.onreconnected(async (connectionId) => {
      console.log("SignalR Reconnected:", connectionId);
      // Rejoin the incident room after reconnection
      if (this.currentIncidentId) {
        await this.joinIncidentRoom(this.currentIncidentId);
      }
      this.updateConnectionState("connected");
    });

    // Handle close
    this.connection.onclose((error) => {
      console.log("SignalR Connection closed", error);
      this.updateConnectionState("disconnected");
      if (error) {
        this.notifyError(error);
      }
    });
  }

  /**
   * Join an incident chat room
   * @param {string|number} incidentId
   */
  async joinIncidentRoom(incidentId) {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      throw new Error("Not connected to SignalR hub");
    }

    try {
      await this.connection.invoke("JoinIncidentRoom", String(incidentId));
      console.log("Joined incident room:", incidentId);
    } catch (error) {
      console.error("Failed to join incident room:", error);
      throw error;
    }
  }

  /**
   * Leave an incident chat room
   * @param {string|number} incidentId
   */
  async leaveIncidentRoom(incidentId) {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      return;
    }

    try {
      await this.connection.invoke("LeaveIncidentRoom", String(incidentId));
      console.log("Left incident room:", incidentId);
    } catch (error) {
      console.error("Failed to leave incident room:", error);
    }
  }

  /**
   * Send a message to the current incident chat
   * @param {string} message - The message content
   * @param {object} metadata - Optional metadata (e.g., incident context)
   */
  async sendMessage(message, metadata = {}) {
    if (
      !this.connection ||
      this.connection.state !== signalR.HubConnectionState.Connected
    ) {
      throw new Error("Not connected to SignalR hub");
    }

    if (!this.currentIncidentId) {
      throw new Error("No incident room joined");
    }

    try {
      await this.connection.invoke("SendMessage", {
        incidentId: String(this.currentIncidentId),
        content: message,
        timestamp: new Date().toISOString(),
        ...metadata,
      });
      console.log("Message sent:", message);
    } catch (error) {
      console.error("Failed to send message:", error);
      throw error;
    }
  }

  /**
   * Stop the SignalR connection
   */
  async stopConnection() {
    this.isConnecting = false; // Reset connecting flag

    if (this.connection) {
      // Leave current room before disconnecting
      if (this.currentIncidentId) {
        try {
          await this.leaveIncidentRoom(this.currentIncidentId);
        } catch (error) {
          console.error("Error leaving room:", error);
        }
      }

      try {
        await this.connection.stop();
        console.log("SignalR Connection stopped");
      } catch (error) {
        console.error("Error stopping connection:", error);
      }

      this.connection = null;
      this.currentIncidentId = null;
      this.updateConnectionState("disconnected");
    }
  }

  /**
   * Subscribe to incoming messages
   * @param {function} callback - Callback function for messages
   * @returns {function} Unsubscribe function
   */
  onMessage(callback) {
    this.messageCallbacks.push(callback);
    return () => {
      this.messageCallbacks = this.messageCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  /**
   * Subscribe to streaming tokens
   * @param {function} callback - Callback function for streaming tokens
   * @returns {function} Unsubscribe function
   */
  onStreamingToken(callback) {
    this.streamingCallbacks.push(callback);
    return () => {
      this.streamingCallbacks = this.streamingCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  /**
   * Subscribe to connection state changes
   * @param {function} callback - Callback function for state changes
   * @returns {function} Unsubscribe function
   */
  onConnectionStateChange(callback) {
    this.connectionStateCallbacks.push(callback);
    // Immediately notify of current state
    callback(this.connectionState);
    return () => {
      this.connectionStateCallbacks = this.connectionStateCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  /**
   * Subscribe to errors
   * @param {function} callback - Callback function for errors
   * @returns {function} Unsubscribe function
   */
  onError(callback) {
    this.errorCallbacks.push(callback);
    return () => {
      this.errorCallbacks = this.errorCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Update and notify connection state
   * @param {string} state
   */
  updateConnectionState(state) {
    this.connectionState = state;
    this.connectionStateCallbacks.forEach((callback) => callback(state));
  }

  /**
   * Notify error callbacks
   * @param {Error} error
   */
  notifyError(error) {
    this.errorCallbacks.forEach((callback) => callback(error));
  }

  /**
   * Get current connection state
   * @returns {string}
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  isConnected() {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Export singleton instance
const chatHubService = new ChatHubService();
export default chatHubService;
